import { useEffect, useRef, useState } from "react";
import type { ECharts, EChartsCoreOption } from "echarts/core";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  RadarComponent,
  DatasetComponent,
  ToolboxComponent
} from "echarts/components";
import { BarChart, RadarChart, LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import * as echarts from "echarts/core";

echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  RadarComponent,
  DatasetComponent,
  ToolboxComponent,
  BarChart,
  RadarChart,
  LineChart,
  CanvasRenderer
]);

interface EChartProps {
  option: EChartsCoreOption | undefined;
  height?: number;
  className?: string;
  onInstance?: (chart: ECharts) => void;
}

export function EChart({ option, height = 320, className, onInstance }: EChartProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    chartRef.current = echarts.getInstanceByDom(container) ?? echarts.init(container);
    setReady(true);
    if (chartRef.current && onInstance) {
      onInstance(chartRef.current);
    }
    const handleResize = () => {
      chartRef.current?.resize();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [onInstance]);

  useEffect(() => {
    if (!ready || !chartRef.current || !option) {
      return;
    }
    chartRef.current.setOption(option, { notMerge: true });
  }, [option, ready]);

  return <div ref={containerRef} style={{ height }} className={className} />;
}
